import { FILTERS } from '@/common/constants';
import { PERMISSIONS } from '@/auth/permissions.constants';
import type { UserOption } from '@/otros-tramites/services/otrosTramitesService';
import type { TramiteFilters, PersistableTramiteFilters } from './types';

const isValidAssignedUser = (value: unknown): value is UserOption => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<UserOption>;
  return typeof candidate.id === 'string' && typeof candidate.nombre === 'string';
};

interface BuildTramitesUrlParams {
  searchQuery?: string;
  sortField?: string;
  sortDirection?: string;
  dateFrom?: string;
  dateTo?: string;
  dateField?: string;
  escrituraFilter?: string;
  registroFilter?: string;
  estadoFilter?: string;
  bloqueadaFilter?: string;
  archivadoFilter?: string;
  assignedUserId?: string;
}

/** Traduce el filtro de la UI al parámetro tri-estado que espera el backend. */
export function toArchivadoParam(archivadoFilter?: string): 'no' | 'si' | 'all' {
  if (archivadoFilter === 'si') return 'si';
  if (archivadoFilter === 'todas') return 'all';
  return 'no';
}

export function buildTramitesUrl({
  searchQuery = '',
  sortField = 'id',
  sortDirection = 'desc',
  dateFrom = '',
  dateTo = '',
  dateField = 'fecini',
  escrituraFilter = '',
  registroFilter = '',
  estadoFilter = '',
  bloqueadaFilter = '',
  archivadoFilter = '',
  assignedUserId = '',
}: BuildTramitesUrlParams): string {
  const encodedSearch = encodeURIComponent(searchQuery);
  let url = `/tramites?search=${encodedSearch}&sort=${sortField}&order=${sortDirection}`;

  if (dateFrom && dateFrom !== 'Invalid Date' && dateFrom.trim() !== '') {
    url += `&date_from=${encodeURIComponent(dateFrom)}`;
  }
  if (dateTo && dateTo !== 'Invalid Date' && dateTo.trim() !== '') {
    url += `&date_to=${encodeURIComponent(dateTo)}`;
  }
  if (dateField) {
    url += `&date_field=${dateField}`;
  }
  if (escrituraFilter && escrituraFilter !== 'todas') {
    url += `&escritura_filter=${encodeURIComponent(escrituraFilter)}`;
  }
  if (registroFilter && registroFilter !== 'todos') {
    url += `&numregistro=${encodeURIComponent(registroFilter)}`;
  }
  if (estadoFilter && estadoFilter !== 'todos') {
    url += `&estado=${encodeURIComponent(estadoFilter)}`;
  }
  if (bloqueadaFilter && bloqueadaFilter !== 'todos') {
    url += `&bloqueado=${bloqueadaFilter === 'si' ? '1' : '0'}`;
  }
  if (assignedUserId) {
    url += `&assigned_user_id=${encodeURIComponent(assignedUserId)}`;
  }

  // Siempre explícito, a diferencia del resto de los filtros: el default del
  // backend también es "sin archivar", pero mandarlo deja la request
  // autodescriptiva y simétrica con la del kanban.
  url += `&archivado=${toArchivadoParam(archivadoFilter)}`;

  return url;
}

export function getDefaultFilters(userPermissions: string[] = []): TramiteFilters {
  const hasDateFilterPermission = userPermissions.includes(PERMISSIONS.FILTER_BY_DATE.name);

  return {
    searchText: '',
    activeSearchText: '',
    sortField: 'id',
    sortDirection: 'desc',
    dateFrom: hasDateFilterPermission ? FILTERS.DATE_FROM_DEFAULT : '',
    dateTo: '',
    dateField: 'fecini',
    escrituraFilter: '',
    registroFilter: '',
    estadoFilter: '',
    bloqueadaFilter: '',
    archivadoFilter: '',
    assignedUserId: '',
    assignedUser: null,
  };
}

export function getInitialFilters(
  userPermissions: string[],
  preferences: any,
): TramiteFilters {
  const savedFilters = preferences?.tramites?.filters;

  if (savedFilters) {
    // Migrate old "firmado" filter value to empty string (shows as "todas")
    const escrituraFilter =
      savedFilters.escrituraFilter === 'firmado'
        ? ''
        : savedFilters.escrituraFilter || '';

    return {
      searchText: '',
      activeSearchText: savedFilters.activeSearchText || '',
      sortField: savedFilters.sortField || 'id',
      sortDirection: savedFilters.sortDirection || 'desc',
      dateFrom: savedFilters.dateFrom || '',
      dateTo: savedFilters.dateTo || '',
      dateField: savedFilters.dateField || 'fecini',
      escrituraFilter,
      registroFilter: savedFilters.registroFilter || '',
      estadoFilter: savedFilters.estadoFilter || '',
      bloqueadaFilter: savedFilters.bloqueadaFilter || '',
      // Las preferencias guardadas antes de esta función no tienen la clave:
      // caen en '' = "sin archivar", que es el comportamiento previo.
      archivadoFilter: savedFilters.archivadoFilter || '',
      assignedUserId: savedFilters.assignedUserId || '',
      assignedUser: isValidAssignedUser(savedFilters.assignedUser) ? savedFilters.assignedUser : null,
    };
  }

  return getDefaultFilters(userPermissions);
}

export function buildPersistableFilters(
  filters: TramiteFilters,
): PersistableTramiteFilters {
  const { searchText, ...persistable } = filters;
  return persistable;
}
